import _String$fromCodePoint from 'babel-runtime/core-js/string/from-code-point';
import _extends from 'babel-runtime/helpers/extends';
import * as datetime from './datetime';
import { getTaskDataForUser } from './task';
import { isAllDayEvent } from './event';
import { reopened, pending, reviewed } from './reviewstatus';
import translations from '../translations';
import injected from '../injected';
import moment from 'moment';

/* ********************************************************************** */
/* ********************************************************************** */
/* ********************** Badges Methods for Tasks* ********************* */
/* ********************************************************************** */
/* ********************************************************************** */

var getPinTime = function getPinTime(task) {
  return task.pin && task.pin.time ? datetime.beautifyStartTime(task.pin.time) : null;
};

export var getEventPinTimeForMobile = function getEventPinTimeForMobile(event) {
  return event.pin && event.pin.time ? datetime.beautifyPinTimeMobile(event.pin.time, event.estimatedTime) : null;
};

var getPinDate = function getPinDate(task) {
  return task.pin && task.pin.date ? datetime.humanizeDate(task.pin.date) : null;
};

var getDuration = function getDuration(task) {
  return task.type === 'event' && task.free ? null : datetime.beautifyDuration(task.estimatedTime);
};

var getWhen = function getWhen(task) {
  return task.when && task.when.date ? datetime.humanizeDate(task.when.date) : null;
};

var getETATime = function getETATime(task) {
  return task.ETA && task.ETA.time ? datetime.beautifyTime(task.ETA.time) : null;
};

var getCompletionTime = function getCompletionTime(task) {
  var timeString = task.completionTime ? translations.general.done + ' ' + translations.task.at.toLowerCase() + ' ' + datetime.beautifyTime(task.completionTime) : null;
  var dateString = task.completionTime ? translations.general.done + ' ' + datetime.timeAgo(task.completionTime, 30) : null;

  return datetime.isToday(task.completionTime) ? timeString : dateString;
};

var getShouldShowWatcher = function getShouldShowWatcher(task) {
  return task.type === 'task' && (!task.to || task.to.userId === injected.user.id) && task.watchers && task.watchers.length > 0;
};

var getDueDate = function getDueDate(task) {
  return task.dueDate ? datetime.humanizeDate(task.dueDate) : null;
};

var getReviewLabel = function getReviewLabel(task) {
  var _getTaskDataForUser = getTaskDataForUser(task, injected.user),
      isReviewTask = _getTaskDataForUser.isReviewTask,
      isAssignee = _getTaskDataForUser.isAssignee,
      isWatcher = _getTaskDataForUser.isWatcher,
      isReviewer = _getTaskDataForUser.isReviewer,
      reviewStatus = _getTaskDataForUser.reviewStatus,
      hasReviewerWithStatusPending = _getTaskDataForUser.hasReviewerWithStatusPending,
      hasReviewerWithStatusReopened = _getTaskDataForUser.hasReviewerWithStatusReopened;

  /* is a review task */


  if (isReviewTask) {
    if (task.reviewStatus === reviewed) {
      return translations.review.badges.reviewed;
    }
    if (task.reviewStatus === reopened) {
      return translations.review.badges.reopened;
    }
    return translations.review.badges.review;
  }

  /* regular task */
  if (!isReviewer && hasReviewerWithStatusReopened) {
    return translations.review.badges.reopened;
  }

  if (!isReviewer && hasReviewerWithStatusPending) {
    return translations.review.badges.waitingReview;
  }

  if (!isAssignee && isWatcher && isReviewer) {
    /* case : Reviewer (teamlist) for original task before completion */
    if (!reviewStatus) {
      return translations.review.badges.willneed;
    }

    /* case : Reviewer (teamlist) for original task after completion but review in pending */
    if (reviewStatus === pending) {
      return translations.review.badges.pending;
    }

    /* case : Reviewer (teamlist) for original task after completion but review in pending */
    if (reviewStatus === reopened) {
      return translations.review.badges.reopened;
    }

    return null;
  }
};

var getReviewBadgeShouldBeBeforeTitle = function getReviewBadgeShouldBeBeforeTitle(task) {
  var _getTaskDataForUser2 = getTaskDataForUser(task, injected.user),
      isReviewTask = _getTaskDataForUser2.isReviewTask;

  return isReviewTask && task.reviewStatus !== reopened && task.reviewStatus !== reviewed;
};

var getTagLabels = function getTagLabels(task) {
  return task.tags && task.tags.map(function (tag) {
    return _extends({
      label: getTagLabel(tag)
    }, tag);
  });
};

export var getTagLabel = function getTagLabel(tag) {
  return _String$fromCodePoint(tag.name.codePointAt(0));
};

export var getBadgesDataForTask = function getBadgesDataForTask(task) {
  return {
    pinTime: getPinTime(task),
    pinDate: getPinDate(task),
    duration: getDuration(task),
    when: getWhen(task),
    ETATime: getETATime(task),
    completionTime: getCompletionTime(task),
    shouldShowWatcher: getShouldShowWatcher(task),
    dueDate: getDueDate(task),
    reviewLabel: getReviewLabel(task),
    reviewBadgeShouldBeBeforeTitle: getReviewBadgeShouldBeBeforeTitle(task),
    tags: getTagLabels(task),
    shouldShowOldTaskBadge: getShouldShowOldTaskBadge(task)
  };
};

var isDeadlinePast = function isDeadlinePast(task, currentTime) {
  return datetime.isToday(task.dueDate) || new Date(task.dueDate) - currentTime < 0;
};

var isPinDatePast = function isPinDatePast(task, currentTime) {
  if (!task.pin || !task.pin.date) {
    return false;
  }
  // NOTE: this is tricky because as explained here https://developer.mozilla.org/fr/docs/Web/JavaScript/Reference/Objets_globaux/Date
  // "La norme ISO 8601 n'est pas complètement respectée car les chaînes qui décrivent
  // uniquement des dates (ex. "1970-01-01") sont considérées en UTC et non en heure locale."
  var utcDate = new Date(task.pin.date);
  var utcDay = utcDate.getUTCDate();
  var utcMonth = utcDate.getUTCMonth();
  var utcYear = utcDate.getUTCFullYear();
  var pinDate = new Date(utcYear, utcMonth, utcDay);
  var pinDateEndOfDay = pinDate.setHours(23, 59, 59, 999);
  return pinDateEndOfDay < currentTime;
};

var isPinTimePast = function isPinTimePast(task, currentTime) {
  var pin = task.pin;

  if (!pin) {
    return false;
  }
  var endDate = new Date(new Date(pin.time).getTime() + 1000 * (task.estimatedTime / datetime.estimatedTimeUnitsBySecond));
  return endDate - currentTime < 0;
};

/* this method should be called every x seconds to have realtime updates */
export var getBadgesTimeDataForTask = function getBadgesTimeDataForTask(task) {
  return {
    isDeadlinePast: isDeadlinePast(task, new Date()),
    isPinDatePast: isPinDatePast(task, new Date()),
    isPinTimePast: isPinTimePast(task, new Date())
  };
};

var getShouldShowOldTaskBadge = function getShouldShowOldTaskBadge(task) {
  var thresholdTime = new Date();
  thresholdTime.setDate(thresholdTime.getDate() - 14);
  return !task.completed && task.watchers && task.watchers.length > 0 && new Date(task.creationTime) - thresholdTime < 0;
};

export var getOldTaskLabel = function getOldTaskLabel(creationTime) {
  var diff = datetime.dateDiff(creationTime, moment());
  var text = translations.tooltip.line.oldTask;

  if (diff.months === 1) {
    text += ' ' + diff.months + ' ' + translations.general.month.singular + ' ' + translations.general.and;
  } else if (diff.months > 1) {
    text += ' ' + diff.months + ' ' + translations.general.month.plural + ' ' + translations.general.and;
  }
  text += ' ';
  if (diff.days === 0) {
    text += ' ' + diff.days + ' ' + translations.general.day.singular;
  } else {
    text += ' ' + diff.days + ' ' + translations.general.day.plural;
  }

  return text;
};

/* ********************************************************************** */
/* ********************************************************************** */
/* ********************** Badges Methods for Events ********************* */
/* ********************************************************************** */
/* ********************************************************************** */

var getEventTimeDetails = function getEventTimeDetails(event, currentTime) {
  var STARTING_SOON_CAP = 30;
  var pin = event.pin && event.pin.time;
  if (isAllDayEvent(event) || !pin) {
    return false;
  }
  var diffMinutes = (new Date(pin).getTime() - currentTime.getTime()) / (1000 * 60);

  var isStartingSoon = diffMinutes >= 0 && diffMinutes <= STARTING_SOON_CAP;
  var isInProgress = diffMinutes <= 0 && -diffMinutes < event.estimatedTime / (60 * datetime.estimatedTimeUnitsBySecond);
  return { isStartingSoon: isStartingSoon, isInProgress: isInProgress, diffMinutes: Math.ceil(diffMinutes) };
};

export var getBadgesDataForEvent = function getBadgesDataForEvent(event) {
  return {
    isAllDay: isAllDayEvent(event)
  };
};

/* this method should be called every x seconds to have realtime updates */
export var getBadgesTimeDataForEvent = function getBadgesTimeDataForEvent(event) {
  return getEventTimeDetails(event, new Date());
};
//# sourceMappingURL=badges.js.map


//////////////////
// WEBPACK FOOTER
// ../gipsy-misc/lib/utils/badges.js
// module id = 728
// module chunks = 0